import {
  Autocomplete,
  Card,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material";
import axios from "axios";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Form, Formik } from "formik";
import CoverLayout from "pages/authentication/components/CoverLayout";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loginUserAction } from "redux/slice/users";
import { enqueueSnackbar } from "notistack";
import { subscriptionActions } from "redux/slice/subscription";
import * as Yup from "yup";

const validationSchema = Yup.object({
  type: Yup.string().required("Login Type is required"),
  company: Yup.object().when("type", {
    is: (type) => type === "proxy-login",
    then: Yup.object()
      .required("Please select company")
      .typeError("Please select company")
      .nullable(),
    otherwise: Yup.object().nullable(),
  }),
  user: Yup.object().when("type", {
    is: (type) => type === "proxy-login",
    then: Yup.object().required("Please select user").typeError("Please select user").nullable(),
    otherwise: Yup.object().nullable(),
  }),
});

function ProxyUser() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const proxyUserState = JSON.parse(localStorage.getItem("proxyUserState"));

  useEffect(() => {
    if (!proxyUserState || proxyUserState?.user?.type !== "admin") {
      navigate("/sign-in", { replace: true });
    }
  }, []);

  useEffect(() => {
    if (proxyUserState?.token) {
      setIsLoading(true);
      axios
        .get(`${process.env.REACT_APP_API_URL}/company/companyListForLogin`, {
          headers: {
            Authorization: `Bearer ${proxyUserState?.token}`,
          },
        })
        .then((response) => {
          if (response.status === 200) {
            setCompanies(response.data.data);
            setIsLoading(false);
          } else {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: "Something went wrong.",
              }),
              {
                variant: "custom",
              }
            );
            navigate("/sign-in", { replace: true });
            setIsLoading(false);
          }
        })
        .catch((err) => {
          setIsLoading(false);
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: err.response.status === 403 ? "Session Expired" : "Error",
              text: err.response.data.message || "Something Went Wrong.",
            }),
            {
              variant: "custom",
            }
          );

          localStorage.removeItem("proxyUserState");

          navigate("/sign-in", { replace: true });
        });
    }
    return () => {
      setIsLoading(false);
      setCompanies([]);
    };
  }, []);

  return (
    <CoverLayout>
      <Card sx={{ mb: 6 }}>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="secondary"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Proxy User Login
          </MDTypography>
        </MDBox>
        <MDBox
          p={3}
          sx={{
            "& .MuiInputLabel-root ": {
              lineHeight: "20px",
            },
          }}
        >
          <Formik
            enableReinitialize
            validateOnChange
            validationSchema={validationSchema}
            initialValues={{
              type: "admin",
              company: null,
              user: null,
            }}
            onSubmit={(data) => {
              if (data.type === "admin") {
                localStorage.setItem("user", JSON.stringify(proxyUserState?.user?.type));
                dispatch(loginUserAction.getIsProxyLogin(true));
                dispatch(
                  loginUserAction.getLoginUserData({
                    ...proxyUserState?.user,
                    emailVerified: true,
                    disabled: false,
                  })
                );
                localStorage.setItem("token", proxyUserState?.token);
                navigate("/registrations", { replace: true });
                localStorage.removeItem("proxyUserState");
              } else {
                axios
                  .post(
                    `${process.env.REACT_APP_API_URL}/user/proxyUserLogin`,
                    {
                      userId: data.user.id,
                      companyId: data.company.id,
                    },
                    {
                      headers: {
                        Authorization: `Bearer ${proxyUserState?.token}`,
                      },
                    }
                  )
                  .then((response) => {
                    if (response.status === 200) {
                      const userObj = response.data.data;
                      userObj.dateFormat = response.data?.dateFormat;
                      dispatch(subscriptionActions.setSubscriptionLoading(true));
                      localStorage.setItem("user", JSON.stringify(response.data.data.type));
                      dispatch(loginUserAction.getIsProxyLogin(true));
                      dispatch(
                        loginUserAction.getLoginUserData({
                          ...userObj,
                          emailVerified: true,
                          disabled: false,
                        })
                      );
                      localStorage.setItem("token", response.data.token);
                      navigate("/home", { replace: true });
                      localStorage.removeItem("proxyUserState");
                    } else {
                      enqueueSnackbar(
                        JSON.stringify({
                          icon: "error",
                          title: "Error",
                          text: "Something went wrong!",
                        }),
                        {
                          variant: "custom",
                        }
                      );

                      localStorage.removeItem("proxyUserState");

                      navigate("/sign-in", { replace: true });
                    }
                  })
                  .catch((err) => {
                    enqueueSnackbar(
                      JSON.stringify({
                        icon: "error",
                        title: err.response.status === 403 ? "Session Expired" : "Error",
                        text: err.response.data.message || "Something Went Wrong.",
                      }),
                      {
                        variant: "custom",
                      }
                    );

                    localStorage.removeItem("proxyUserState");

                    navigate("/sign-in", { replace: true });
                  });
              }
            }}
          >
            {({ values, setFieldValue, touched, errors }) => (
              <Form>
                <Stack spacing={1.5}>
                  <FormControl
                    sx={{
                      "& .Mui-focused": {
                        color: "#7b809a !important",
                      },
                    }}
                    fullWidth
                  >
                    <FormLabel id="demo-controlled-radio-buttons-group">Login As</FormLabel>
                    <RadioGroup
                      aria-labelledby="demo-controlled-radio-buttons-group"
                      name="type"
                      value={values.type}
                      onChange={(event) => {
                        setFieldValue("type", event.target.value);
                      }}
                    >
                      <FormControlLabel value="admin" control={<Radio />} label="Corporate admin" />
                      <FormControlLabel
                        value="proxy-login"
                        control={<Radio />}
                        label="Proxy user"
                      />
                    </RadioGroup>
                  </FormControl>
                  {values.type === "proxy-login" && (
                    <>
                      <Autocomplete
                        disableClearable
                        id="company"
                        value={values?.company}
                        options={companies}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            {option.name}
                          </li>
                        )}
                        onChange={(_, value) => {
                          setFieldValue("company", value);
                          if (values.user) {
                            setFieldValue("user", null);
                          }
                        }}
                        fullWidth
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Company"
                            name="company"
                            id="company"
                            error={touched.company && Boolean(errors.company)}
                            helperText={touched.company && errors.company}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                      <Autocomplete
                        disableClearable
                        id="user"
                        value={values?.user}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        options={
                          companies
                            ?.find((company) => company?.id === values?.company?.id)
                            ?.UserCompaniesLinks?.filter(
                              (userCompaniesLink) => userCompaniesLink?.user?.type === "user"
                            ) || []
                        }
                        onChange={(_, value) => {
                          setFieldValue("user", value);
                        }}
                        fullWidth
                        renderOption={(props, option) => {
                          const usersPermissionProfile = option?.PermissionProfile?.profile_name;
                          return (
                            <li {...props} key={option.id}>
                              {option.name}
                              {usersPermissionProfile && (
                                <span
                                  style={{
                                    fontStyle: "italic",
                                    marginLeft: "16px",
                                  }}
                                >
                                  ( {usersPermissionProfile} )
                                </span>
                              )}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="User"
                            id="user"
                            name="user"
                            error={touched.user && Boolean(errors.user)}
                            helperText={touched.user && errors.user}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isLoading ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                    </>
                  )}
                </Stack>
                <MDBox mt={2} mb={1}>
                  <MDButton color="primaryRed" fullWidth type="submit">
                    log in
                  </MDButton>
                </MDBox>
              </Form>
            )}
          </Formik>
        </MDBox>
      </Card>
    </CoverLayout>
  );
}

export default ProxyUser;
